import * as React from "react";
import fetch from "lib/fetch";
import { ICollectionFeedItem } from "reducers/timeline";
import { IErrorResponse } from "reducers/base-reducers";
export const ONE_MINUTE = 60000;

interface IResponse {
    activites: ICollectionFeedItem[];
}

export const useFeedPolling = ({
    url,
    interval,
    retryCount,
    onSuccess,
    onFailure,
}: {
    url?: string;
    interval?: number;
    retryCount?: number;
    onSuccess?: (response: IResponse) => void;
    onFailure?: (error: IErrorResponse) => void;
}) => {
    const [isPolling, setIsPolling] = React.useState<boolean>(false);
    const isPollingRef = React.useRef<boolean>();
    isPollingRef.current = isPolling;
    const isMounted = React.useRef<boolean>();
    const poll = React.useRef<number | NodeJS.Timeout>();
    const urlRef = React.useRef<string>(url);
    urlRef.current = url;

    const shouldRetry = retryCount > 0;

    const stopPolling = () => {
        if (isMounted.current) {
            if (poll.current) {
                clearTimeout(poll.current);
                poll.current = null;
            }
            setIsPolling(false);
        }
    };

    const startPolling = () => {
        if (urlRef.current && onSuccess) {
            setIsPolling(true);
            runPolling();
        }
    };

    const runPolling = () => {
        const timeoutId = setTimeout(() => {
            fetch(urlRef.current, {
                method: "GET",
                noDomain: true,
            })
                .then((response) => {
                    return response.json().then((data) => {
                        if (response.ok) {
                            return data;
                        } else {
                            return Promise.reject({ status: response.status, data });
                        }
                    });
                })
                .then((data) => {
                    onSuccess(data);
                    return true;
                })
                .then((continuePolling) => {
                    isPollingRef.current && continuePolling ? runPolling() : stopPolling();
                })
                .catch((error) => {
                    if (onFailure) {
                        onFailure(error);
                    }
                    if (shouldRetry && retryCount > 0) {
                        retryCount = retryCount - 1;
                        runPolling();
                    } else {
                        stopPolling();
                    }
                });
        }, interval);
        poll.current = timeoutId;
    };

    React.useEffect(() => {
        isMounted.current = true;
        startPolling();
        return () => {
            isMounted.current = false;
            stopPolling();
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    return [isPolling, startPolling, stopPolling];
};
